import React, { useEffect, useState } from "react";
import { api, formatTRY } from "@/lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TableWrap, Thead, EmptyRow } from "@/components/DataTable";

const emptyForm = { name: "", role: "Usta", weekly_salary: 0, phone: "", active: true };

export default function Employees() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    const res = await api.get("/employees");
    setItems(res.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name,
      role: e.role || "Usta",
      weekly_salary: e.weekly_salary,
      phone: e.phone || "",
      active: e.active,
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) {
      toast.error("İsim gerekli");
      return;
    }
    const payload = { ...form, weekly_salary: Number(form.weekly_salary) || 0 };
    if (editing) {
      await api.put(`/employees/${editing.id}`, payload);
      toast.success("Güncellendi");
    } else {
      await api.post("/employees", payload);
      toast.success("Eklendi");
    }
    setOpen(false);
    load();
  };
  const del = async () => {
    await api.delete(`/employees/${deleteId}`);
    toast.success("Silindi");
    setDeleteId(null);
    load();
  };

  const totalWeekly = items.filter((e) => e.active).reduce((s, e) => s + (e.weekly_salary || 0), 0);
  const monthly = totalWeekly * 4;

  return (
    <div data-testid="employees-page" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Aktif Çalışan</p>
          <p className="font-display text-3xl font-bold text-slate-900 mt-2 font-mono-num" data-testid="active-employee-count">
            {items.filter((e) => e.active).length}
          </p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Toplam Haftalık Maaş</p>
          <p className="font-display text-3xl font-bold text-slate-900 mt-2 font-mono-num" data-testid="total-weekly-salary">
            {formatTRY(totalWeekly)}
          </p>
        </div>
        <div className="bg-slate-900 text-white border border-slate-900 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Aylık Yaklaşık (4 hafta)</p>
          <p className="font-display text-3xl font-bold mt-2 font-mono-num text-white">
            {formatTRY(monthly)}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">Çalışanlar</h2>
          <p className="text-sm text-slate-500">Haftalık sabit maaş modeli</p>
        </div>
        <Button data-testid="add-employee-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800">
          <Plus className="w-4 h-4 mr-2" /> Yeni Çalışan
        </Button>
      </div>

      <TableWrap>
        <Thead columns={["Ad Soyad", "Görev", "Telefon", "Haftalık Maaş", "Durum", ""]} />
        <tbody>
          {items.length === 0 && (
            <EmptyRow colSpan={6} testId="employees-empty" message="Henüz çalışan yok. Yeni çalışan ekleyin." />
          )}
          {items.map((e) => (
            <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`employee-row-${e.id}`}>
              <td className="px-4 py-3 font-semibold text-slate-900">{e.name}</td>
              <td className="px-4 py-3 text-sm text-slate-700">{e.role}</td>
              <td className="px-4 py-3 text-sm text-slate-700">{e.phone || "-"}</td>
              <td className="px-4 py-3 text-sm font-mono-num font-semibold text-slate-900">{formatTRY(e.weekly_salary)}</td>
              <td className="px-4 py-3">
                {e.active ? (
                  <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-medium">
                    Aktif
                  </span>
                ) : (
                  <span className="inline-block px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 text-xs font-medium">
                    Pasif
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button data-testid={`edit-employee-${e.id}`} variant="ghost" size="icon" onClick={() => openEdit(e)} className="h-8 w-8">
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </Button>
                  <Button data-testid={`delete-employee-${e.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(e.id)} className="h-8 w-8 hover:bg-rose-50">
                    <Trash2 className="w-4 h-4 text-rose-600" />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="employee-dialog" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Çalışanı Düzenle" : "Yeni Çalışan"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Ad Soyad</Label>
              <Input
                data-testid="employee-name-input"
                value={form.name}
                onChange={(ev) => setForm({ ...form, name: ev.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Görev</Label>
                <Input
                  data-testid="employee-role-input"
                  value={form.role}
                  onChange={(ev) => setForm({ ...form, role: ev.target.value })}
                  placeholder="Boyacı / Kaportacı / Usta"
                />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input
                  data-testid="employee-phone-input"
                  value={form.phone}
                  onChange={(ev) => setForm({ ...form, phone: ev.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Haftalık Maaş (₺)</Label>
              <Input
                data-testid="employee-salary-input"
                type="number"
                step="0.01"
                value={form.weekly_salary}
                onChange={(ev) => setForm({ ...form, weekly_salary: ev.target.value })}
                className="font-mono-num"
              />
            </div>
            <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-3">
              <div>
                <p className="text-sm font-medium text-slate-800">Aktif Çalışan</p>
                <p className="text-xs text-slate-500">Pasif çalışanlar toplama dahil edilmez.</p>
              </div>
              <Switch
                data-testid="employee-active-switch"
                checked={form.active}
                onCheckedChange={(v) => setForm({ ...form, active: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="save-employee-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800">
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Çalışan silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz. Maaş ödemeleri silinmez.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-employee" onClick={del} className="bg-rose-600 hover:bg-rose-700">
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
